import { useEffect, useState, useCallback } from "react";

type Theme = "light" | "dark";

function getInitialTheme(): Theme {
  if (typeof document === "undefined") return "dark";
  const stored = localStorage.getItem("theme") as Theme | null;
  if (stored) return stored;
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function applyTheme(next: Theme, save: boolean = true) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (next === "dark") root.classList.add("dark");
  else root.classList.remove("dark");

  if (save) {
    try {
      localStorage.setItem("theme", next);
    } catch {}
  }
}

type SetThemeOptions = { save?: boolean };

export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(getInitialTheme);

  // Sync from DOM after hydration and listen for changes
  useEffect(() => {
    const initial = getInitialTheme();
    setThemeState(initial);
    applyTheme(initial, false); // Ensure DOM matches state on mount

    // Listen for manual class changes on <html> (from any useTheme instance)
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === "class") {
          const isDark = document.documentElement.classList.contains("dark");
          setThemeState(isDark ? "dark" : "light");
        }
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    // Listen for system changes
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = () => {
      const stored = localStorage.getItem("theme");
      if (!stored) {
        const next = mediaQuery.matches ? "dark" : "light";
        applyTheme(next, false); // Don't save to localStorage for system changes
      }
    };

    mediaQuery.addEventListener("change", handleChange);

    return () => {
      observer.disconnect();
      mediaQuery.removeEventListener("change", handleChange);
    };
  }, []);

  const toggle = useCallback(() => {
    const current = getInitialTheme();
    const next: Theme = current === "dark" ? "light" : "dark";
    applyTheme(next);
    setThemeState(next);
  }, []);

  const setTheme = useCallback((next: Theme, options?: SetThemeOptions) => {
    applyTheme(next, options?.save !== false);
    setThemeState(next);
  }, []);

  return { theme, toggle, setTheme };
}
